// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Sync Manager (Orchestrator)
// Starts and stops all background sync services: heartbeat, offline queue,
// reservation sync, and table sync. Also ensures a cash session is active.
// ---------------------------------------------------------------------------

import { heartbeatService } from '@/services/heartbeat';
import { offlineQueue } from '@/services/offline-queue';
import { reservationSync } from '@/services/reservation-sync';
import { tableSync } from '@/services/table-sync';
import { apiClient } from '@/services/api-client';
import { useCashStore } from '@/store/cash-store';

// ---------------------------------------------------------------------------
// Sync Manager
// ---------------------------------------------------------------------------

/**
 * Start all background sync services.
 * Should be called once, after the user is authenticated.
 */
export async function startAll(): Promise<void> {
  console.log('[SyncManager] Starting all background services...');

  // Initialize offline queue (opens IndexedDB)
  await offlineQueue.init();

  // Start heartbeat
  heartbeatService.start();

  // Start offline queue background sync
  offlineQueue.startBackgroundSync();

  // Initialize and start reservation sync
  await reservationSync.initDatabase();
  await reservationSync.loadFromCache();
  reservationSync.startAutoSync();

  // Start table sync
  tableSync.startAutoSync();

  console.log('[SyncManager] All services started');
}

/**
 * Stop all background sync services.
 * Should be called on logout or component unmount.
 */
export function stopAll(): void {
  console.log('[SyncManager] Stopping all background services...');

  heartbeatService.stop();
  offlineQueue.stop();
  reservationSync.stopAutoSync();
  tableSync.stopAutoSync();

  console.log('[SyncManager] All services stopped');
}

/**
 * Ensure a cash session is active for NF525 compliance.
 *
 * 1. Checks if a session already exists on the server for this POS.
 * 2. If not, automatically opens one using the POS cash_float config.
 *
 * Non-blocking: the POS continues to operate even if this fails.
 */
export async function ensureCashSession(): Promise<void> {
  try {
    // 1. Check for existing session
    const result = await apiClient.getCurrentCashSession();
    if (result.session) {
      useCashStore.getState().setCashSession(result.session.id, result.session);
      console.log('[SyncManager] Active cash session:', result.session.id);
      return;
    }

    // 2. No session -- auto-open with POS cash_float
    console.log('[SyncManager] No active session -- opening automatically...');
    let openingAmount = 0;

    try {
      const posConfig = await apiClient.getCaisseConfig();
      if (posConfig?.cash_float && typeof posConfig.cash_float === 'object') {
        const cf = posConfig.cash_float as Record<string, number>;
        openingAmount =
          (cf.coins ?? 0) +
          (cf.bill_5 ?? 0) * 5 +
          (cf.bill_10 ?? 0) * 10 +
          (cf.bill_20 ?? 0) * 20 +
          (cf.bill_50 ?? 0) * 50 +
          (cf.bill_100 ?? 0) * 100 +
          (cf.bill_200 ?? 0) * 200 +
          (cf.bill_500 ?? 0) * 500;
      } else if (typeof posConfig?.cash_float === 'number') {
        openingAmount = posConfig.cash_float;
      }
    } catch {
      console.warn('[SyncManager] Could not read POS cash_float config');
    }

    const openResult = await apiClient.openCashSession(openingAmount);
    if (openResult.session) {
      useCashStore
        .getState()
        .setCashSession(openResult.session.id, openResult.session);
      console.log(
        '[SyncManager] Cash session opened:',
        openResult.session.id,
        '-- float:',
        openingAmount,
        'EUR',
      );
    }
  } catch (error) {
    console.error(
      '[SyncManager] Cash session error:',
      (error as Error).message,
    );
    // Non-blocking: POS continues without a session (graceful degradation)
  }
}
